/**
 * AuthContext — global auth state for the IndexingTool frontend.
 *
 * After login, if the user does not have at least a "broker" role the login
 * is rejected. Role hierarchy: admin > broker > user.
 * Admin-only features (like user management) are gated separately in the UI.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import apiClient from '../utils/api';
import { setAccessToken, clearAccessToken } from '../utils/api';

const AuthContext = createContext(null);

/* Mirrors ROLE_LEVELS in auth/middleware/auth_middleware.py — including the
 * ordering, which is load-bearing: company_broker sits BELOW broker so that
 * every require_min_role("broker") route rejects external staff by default.
 *
 * This map is duplicated from the backend, and it had drifted: company_broker
 * was missing entirely, so an external account fell through `?? 0` and read as
 * a plain user. The login gate then refused the very people the company pages
 * were built for. If a role is added on the server, it has to be added here too
 * — a missing entry fails silently rather than loudly.
 */
const ROLE_LEVELS = { user: 0, company_broker: 1, broker: 2, admin: 3 };

function highestRole(roles) {
  if (!Array.isArray(roles)) return 0;
  return Math.max(...roles.map((r) => ROLE_LEVELS[r] ?? 0), 0);
}

/** Internal staff — the back office: every compound, every unit, admin tools. */
const hasBrokerAccess = (u) => highestRole(u?.roles) >= ROLE_LEVELS.broker;

/** Check if user is admin */
const isAdminUser = (u) => highestRole(u?.roles) >= ROLE_LEVELS.admin;

/** Staff of an external brokerage — their own inventory, leads and team.
 *
 * company_broker deliberately ranks BELOW broker, which is what makes every
 * require_min_role("broker") route reject them by default. That ordering is
 * correct and stays. But it also meant this console refused them at the login
 * gate, while the API was already granting them a scoped set of routes — so
 * the people the company surfaces were built for could not reach the door.
 *
 * Admitting them here is not what keeps them out of the back office: each
 * route enforces its own scope server-side (auth/company_route_policy.py).
 * Hiding pages they cannot use is a courtesy, not the boundary.
 */
const isCompanyStaff = (u) => {
const level = highestRole(u?.roles);
  return level === ROLE_LEVELS.company_broker;
};

/** May sign in to this console at all. */
const canSignIn = (u) => hasBrokerAccess(u) || isCompanyStaff(u);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // initial silent-refresh

  // ── helpers ────────────────────────────────────────────────────────

  /** Silent refresh (cookie → new access token + user). */
  const silentRefresh = useCallback(async () => {
    try {
      const { data } = await apiClient.post('/auth/refresh');
      if (!canSignIn(data.user)) {
        clearAccessToken();
        setUser(null);
        return false;
      }
      setAccessToken(data.access_token);
      setUser(data.user);
      return true;
    } catch {
      clearAccessToken();
      setUser(null);
      return false;
    }
  }, []);

  /** Email / password login — rejects users without broker+ role. */
  const login = useCallback(async (email, password) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    if (!canSignIn(data.user)) {
      clearAccessToken();
      throw new Error(
        'This account cannot sign in here. Ask an administrator for access.'
      );
    }
    setAccessToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  /** Logout. */
  const logout = useCallback(async () => {
    try { await apiClient.post('/auth/logout'); } catch { /* best effort */ }
    clearAccessToken();
    setUser(null);
  }, []);

  // ── On mount: attempt silent refresh ──────────────────────────────
  useEffect(() => {
    silentRefresh().finally(() => setLoading(false));
  }, [silentRefresh]);

  const isAdmin = useMemo(() => isAdminUser(user), [user]);
  // Staff of an external brokerage. Drives what the navigation offers — the
  // server decides what they can actually reach.
  const isCompany = useMemo(() => isCompanyStaff(user), [user]);
  const isInternal = useMemo(() => hasBrokerAccess(user), [user]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      isAdmin,
      isCompany,
      isInternal,
      login,
      logout,
    }),
    [user, loading, login, logout, isAdmin, isCompany, isInternal],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
